import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, forwardRef } from "@angular/core";
import { AbstractControl, ControlValueAccessor, FormBuilder, FormControl, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors } from "@angular/forms";
import { NzStatus } from "ng-zorro-antd/core/types";
import { identity, noop, takeUntil } from "rxjs";
import { DestroyService } from "@webapp/core/util/services/destroy.service";

@Component({
  selector: "metric-number-input-field",
  templateUrl: "./metric-number-input-field.component.html",
  styleUrls: ["./metric-number-input-field.component.less"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    DestroyService,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MetricNumberInputFieldComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: MetricNumberInputFieldComponent,
    },
  ],
})
export class MetricNumberInputFieldComponent implements OnInit, OnChanges, ControlValueAccessor {
  public onChange = identity;
  public onTouch = noop;

  @Input() public focus = false;
  @Input() public uiId: string;
  @Input() public errorMessageId?: string;
  @Input() public selected: number;
  @Input() public placeholder = "";
  @Input() public prefix: string;
  @Input() public suffix: string;
  @Input() public disabled: boolean;
  @Input() public a11yLabel: string;
  @Input() public a11yLabelledby: string;
  @Input() public a11yDescription: string;
  @Input() public a11yRequired: boolean;
  @Input() public max = Infinity;
  @Input() public defaultValue?: number;
  @Input() public e2eTestId: string;
  @Input() public status?: NzStatus;
  @Output() public readonly selectedChange = new EventEmitter<number>();

  public selectForm: FormGroup;

  constructor(private formBuilder: FormBuilder, private destroyService$: DestroyService) {}

  public ngOnInit(): void {
    this.selectForm = this.formBuilder.group({
      number: new FormControl({ value: this.selected, disabled: this.disabled }),
    });
    this.subscribeToValueChange();
  }

  public writeValue(value: number): void {
    this.setValue(value, { emitEvent: false });
  }

  public registerOnChange(fn: typeof identity): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: typeof noop): void {
    this.onTouch = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;

    if (isDisabled) {
      this.selectForm.disable();
    } else {
      this.selectForm.enable();
    }
  }

  public markAsTouched(): void {
    this.onTouch();
  }

  private setValue(value: number | null, options: { emitEvent: boolean } = { emitEvent: true }): void {
    this.selectForm.get("number").patchValue(value, { emitEvent: options.emitEvent });
  }

  public validate(control: AbstractControl): ValidationErrors | null {
    if (!control.value && control.value !== 0) {
      return {
        zeroOrTruthy: {
          value: control.value,
        },
      };
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.selected && !changes.selected.firstChange) {
      this.setValue(changes.selected.currentValue, { emitEvent: false });
    }
  }

  public blurHandler(): void {
    const { value } = this.selectForm.get("number");

    if ((this.defaultValue !== undefined || this.defaultValue === 0) && !value && value !== 0) {
      this.setValue(this.defaultValue);
      this.markAsTouched();
    }
  }

  private subscribeToValueChange(): void {
    this.selectForm.controls["number"].valueChanges.pipe(takeUntil(this.destroyService$)).subscribe((value) => {
      this.onChange(value);
      this.markAsTouched();
      this.selectedChange.emit(value);
    });
  }
}
